import { Pagination, Tooltip } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { FiEdit2, FiFileText, FiSend, FiCheckCircle, FiClock } from "react-icons/fi";
import { useHistory } from "react-router-dom";
import toast from "react-hot-toast";
import { Button, Input, Spinner, Table } from "reactstrap";
import { sendingEmail } from "../../api/sendEmailPdf.api";
import useRowSelection from "../../views/Admin/customHooks/useRowSelection";
import NotFoundDataUI from "../cards/NotFoundDataUI";

const QuotesTable = ({
  data,
  setSelectedData,
  user,
  page,
  setPage,
  pageSize,
  setPageSize,
  totalPages,
  onClearSearch,
}) => {
  const history = useHistory();
  const [emailLoading, setEmailLoading] = useState(null); // Track which row is loading
  const {
    selectedRows,
    handleCheckboxChange,
    handleSelectAll,
    handleViewSelected,
  } = useRowSelection();

  useEffect(() => {
    let selected = handleViewSelected(data);
    setSelectedData(selected);
  }, [selectedRows, data]);

  const resentEmail = async (row) => {
    if (row?.pdfLink && row?.sendPdf === true) {
      setEmailLoading(row._id);
      const obj = {
        email: row?.customer?.billing?.email,
        pdfLink: row?.pdfLink,
        documentType: "Quote",
        documentNumber: row?.id,
      };
      await sendingEmail(obj, (res) => {
        setEmailLoading(null);
        if (res?.status === 200) {
          toast.success("PDF Sent Successfully");
        } else {
          toast.error("PDF could not be sent at this time.");
        }
      });
    }
  };

  return (
    <div>
      <Table
        responsive
        hover
        bordered
        className="mb-0"
        style={{
          fontSize: "15px",
          overflow: "hidden",
          border: "1px solid #e2e8f0"
        }}
      >
        <thead>
          <tr style={{ backgroundColor: "#00509d", color: "white" }}>
            {(user?.role === "Admin" || user?.role === "Super-admin") && (
              <th className="text-center" style={{ width: '50px', borderBottom: "none" }}>
                <Input
                  type="checkbox"
                  onChange={(e) => handleSelectAll(e, data)}
                  checked={data.length > 0 && selectedRows.length === data.length}
                />
              </th>
            )}
            <th className="text-center" style={{ borderBottom: "none" }}>Date</th>
            <th className="text-center" style={{ borderBottom: "none" }}>Quote ID</th>
            <th className="text-center" style={{ borderBottom: "none" }}>Organization</th>
            <th className="text-center" style={{ borderBottom: "none" }}>Creator</th>
            <th className="text-center" style={{ borderBottom: "none" }}>Total Amount</th>
            <th className="text-center" style={{ borderBottom: "none" }}>Action</th>
          </tr>
        </thead>

        <tbody>
          {data && data.length > 0 ? (
            data.map((row, index) => (
              <tr
                key={index}
                className="align-middle hover-glow"
                style={{
                  backgroundColor: index % 2 === 0 ? "#ffffff" : "#f8fafc",
                  borderBottom: "1px solid #f1f5f9"
                }}
              >
                {(user?.role === "Admin" || user?.role === "Super-admin") && (
                  <td className="text-center">
                    <Input
                      type="checkbox"
                      onChange={(e) => handleCheckboxChange(e, row)}
                      checked={selectedRows.includes(row._id)}
                    />
                  </td>
                )}
                <td className="text-center">
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <FiClock className="text-muted small" />
                    <span className="small fw-500">{moment(row.created_at).format("ll")}</span>
                  </div>
                </td>
                <td className="text-center">
                  <span className="user-badge bg-primary-soft text-primary">
                    {row?.type === "manual" ? "M-" : ""}Q#{row.id}
                  </span>
                </td>
                <td className="text-center">
                  <div className="fw-bold">{row.customer?.billing?.company || "Personal Quote"}</div>
                  <div className="text-muted small italic">{row.customer?.billing?.email || "No Email"}</div>
                </td>
                <td className="text-center">
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <FiCheckCircle className="text-success small" />
                    <span className="small">{typeof row.created_by === 'object' ? (row.created_by?.name || row.created_by?.username || "System") : (row.created_by || "System")}</span>
                  </div>
                </td>
                <td className="text-center fw-bold text-success">
                  ${parseFloat(row?.totalAmount ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </td>
                <td className="text-center">
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    {/* Resend/Send */}
                    <Tooltip title={row?.sendPdf && row?.pdfLink ? "Resend PDF" : "Send PDF"}>
                      <Button
                        color="link"
                        className={`p-1 border-0 ${emailLoading === row._id ? 'opacity-50 pointer-events-none' : ''}`}
                        onClick={() =>
                          row?.sendPdf && row?.pdfLink
                            ? resentEmail(row)
                            : history.push({
                              pathname: `/admin/quote-pdf/${row._id}`,
                              data: row,
                              id: row?._id,
                            })
                        }
                      >
                        {emailLoading === row._id ? <Spinner size="sm" /> : <FiSend className="text-secondary" size={16} />}
                      </Button>
                    </Tooltip>

                    {/* Edit */}
                    <Tooltip title="Edit Quote">
                      <Button
                        color="link"
                        className="p-1 border-0"
                        onClick={() => {
                          const path = row?.type === "manual" ? "manual-quote" : "new-quote";
                          history.push({
                            pathname: `/admin/${path}/${row._id}`,
                            data: row,
                            id: row?._id,
                          });
                        }}
                      >
                        <FiEdit2 className="text-primary" size={14} />
                      </Button>
                    </Tooltip>

                    {/* PDF View */}
                    <Tooltip title="View PDF">
                      <Button
                        color="link"
                        className="p-1 border-0"
                        onClick={() =>
                          history.push({
                            pathname: `/admin/quote-pdf/${row._id}`,
                            data: row,
                            id: row?._id,
                          })
                        }
                      >
                        <FiFileText className="text-danger" size={16} />
                      </Button>
                    </Tooltip>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={(user?.role === "Admin" || user?.role === "Super-admin") ? 7 : 6} className="text-center py-4">
                <NotFoundDataUI 
                  message="No quotes found!" 
                  onClearSearch={onClearSearch}
                />
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {data && data.length > 0 && (
        <div className="d-flex justify-content-end mt-3 px-2">
          <Pagination
            size="small"
            current={page}
            total={totalPages}
            onChange={(value) => {
              localStorage.setItem("quotePage", value);
              setPage(value);
            }}
            pageSize={pageSize}
            showSizeChanger={true}
            onShowSizeChange={(current, size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={['10', '25', '50', '100']}
          />
        </div>
      )}
    </div>
  );
};

export default QuotesTable;
